import { Avatar, Badge, Divider, Group, Modal, SimpleGrid, Stack, Text } from '@mantine/core';
import {
  IconCake, IconGenderBigender, IconId, IconMail, IconPhone, IconPhoneCall, IconUser,
} from '@tabler/icons-react';
import { resolveAvatarSrc, getAvatarInitials, reportAvatarLoadError } from '@/utils/avatar';

/**
 * MemberDetailModal — read-only detail for a member attached to a case or a registration.
 *
 * The counselee grid (`Counselling/Cases/[CaseDetails]/Tabs/CounseleesTab.tsx`) and the
 * participant grid (`Grow/Registration/[RegistrationDetails]/Tabs/MembersTab.tsx`) both render
 * cramped cards that can only show a name and one contact line. Clicking a card opens this instead
 * of navigating away from the case or registration.
 *
 * Props are deliberately permissive: both tabs declare their own local `Member` interface against
 * the same `searchMembers` payload, and the portal views spell the surname `surname` rather than
 * `lastName`. Anything missing renders as a "not on record" line rather than being hidden, so the
 * modal reads the same for every member.
 */

export interface MemberDetailFields {
  memberID?: number | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  /** Portal views use `surname`; member search uses `lastName`. Either is accepted. */
  surname?: string | null;
  email?: string | null;
  cellNumber?: string | null;
  telephone?: string | null;
  altContact?: string | null;
  gender?: string | null;
  dateOfBirth?: string | number | null;
  avatar?: string | null;
  memberCode?: string | null;
  isMain?: boolean;
  hasConsent?: boolean;
  isActive?: boolean;
  /** Member type name, when the caller has resolved it from the type lookup. */
  typeName?: string | null;
}

interface MemberDetailModalProps {
  member: MemberDetailFields | null;
  opened: boolean;
  onClose: () => void;
  /** Wording differs by context: "case", "registration", "request", "intake". */
  contextLabel?: string;
  /**
   * Optional banner above the detail grid.
   *
   * Used by the request and intake queues, where the person may be a staged public submission rather
   * than a real member yet — the notice says so, instead of the blank rows implying a thin record.
   */
  notice?: React.ReactNode;
  /** Replaces "Attached to this {contextLabel}" when the plainer wording would be wrong. */
  attachmentLabel?: React.ReactNode;
  /**
   * Replaces the default member grid.
   *
   * ⚠️ For a person who is **not a member yet** — a staged care-request subject or intake participant —
   * four of the eight default rows (telephone, date of birth, member code, member ID) can only ever
   * read "Not on record", because those fields live on the Member record that does not exist. Callers
   * in that situation pass the rows their own table actually carries instead.
   */
  rows?: MemberDetailRow[];
  /** Overrides the "Member Details" heading. */
  title?: string;
}

/** One label/value line in the detail grid. */
export interface MemberDetailRow {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

/**
 * `yyyyMMdd…` or an ISO date to `yyyy-MM-dd`. Members arrive in both shapes, and staged
 * request/intake people carry an ISO string, so both callers need the same handling.
 */
export function formatBirthDate(value: string | number | null | undefined): string | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  if (/^\d{8}/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

function DetailRow({
  icon, label, value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <div style={{ color: 'var(--mantine-color-dimmed)', marginTop: 2 }}>{icon}</div>
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed">{label}</Text>
        <Text size="sm" fw={600} style={{ wordBreak: 'break-word' }}>{value}</Text>
      </Stack>
    </Group>
  );
}

export function MemberDetailModal({
  member,
  opened,
  onClose,
  contextLabel = 'case',
  notice,
  attachmentLabel,
  rows,
  title = 'Member Details',
}: MemberDetailModalProps) {
  const first = member?.firstName ?? '';
  const last = member?.lastName ?? member?.surname ?? '';
  const fullName = [first, member?.middleName, last].filter(Boolean).join(' ').trim()
    || (member?.memberID ? `Member ${member.memberID}` : 'Member');

  const avatarSrc = resolveAvatarSrc(member?.avatar);
  const birthDate = formatBirthDate(member?.dateOfBirth);
  const none = <Text span size="sm" c="dimmed" fw={400}>Not on record</Text>;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{title}</Text>}
      size="lg"
      centered
    >
      {!member ? null : (
        <Stack gap="md">
          <Group gap="md" align="flex-start" wrap="nowrap">
            <Avatar
              src={avatarSrc}
              alt={fullName}
              size={72}
              radius="md"
              imageProps={{ onError: () => reportAvatarLoadError(avatarSrc) }}
            >
              {getAvatarInitials(first, last)}
            </Avatar>

            <Stack gap={6} style={{ minWidth: 0 }}>
              <Text fw={700} size="lg" style={{ wordBreak: 'break-word' }}>{fullName}</Text>
              <Group gap={6}>
                {member.isMain && (
                  <Badge size="sm" variant="light" color="blue">Main participant</Badge>
                )}
                {member.typeName && (
                  <Badge size="sm" variant="light" color="grape">{member.typeName}</Badge>
                )}
                <Badge
                  size="sm"
                  variant="light"
                  color={member.hasConsent ? 'green' : 'orange'}
                >
                  {member.hasConsent ? 'Consent given' : 'No consent recorded'}
                </Badge>
                {member.isActive === false && (
                  <Badge size="sm" variant="light" color="red">Inactive</Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                {attachmentLabel ?? `Attached to this ${contextLabel}`}
              </Text>
            </Stack>
          </Group>

          {notice}

          {/* An explicitly empty `rows` means the caller has nothing to show — the notice carries the
              whole story — so the rule and an empty grid would just look like a rendering fault. */}
          {rows?.length === 0 ? null : (
          <>
          <Divider />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {rows
              ? rows.map((r) => (
                <DetailRow key={r.label} icon={r.icon} label={r.label} value={r.value || none} />
              ))
              : (
                <>
                  <DetailRow icon={<IconMail size={18} />} label="Email" value={member.email || none} />
                  <DetailRow
                    icon={<IconPhone size={18} />}
                    label="Cell"
                    value={member.cellNumber || none}
                  />
                  <DetailRow
                    icon={<IconPhoneCall size={18} />}
                    label="Telephone"
                    value={member.telephone || none}
                  />
                  <DetailRow
                    icon={<IconPhoneCall size={18} />}
                    label="Alternative contact"
                    value={member.altContact || none}
                  />
                  <DetailRow
                    icon={<IconGenderBigender size={18} />}
                    label="Gender"
                    value={member.gender || none}
                  />
                  <DetailRow
                    icon={<IconCake size={18} />}
                    label="Date of birth"
                    value={birthDate ?? none}
                  />
                  <DetailRow
                    icon={<IconId size={18} />}
                    label="Member code"
                    value={member.memberCode || none}
                  />
                  <DetailRow
                    icon={<IconUser size={18} />}
                    label="Member ID"
                    value={member.memberID ?? none}
                  />
                </>
              )}
          </SimpleGrid>
          </>
          )}
        </Stack>
      )}
    </Modal>
  );
}

export default MemberDetailModal;
